import React from 'react';

export type SePayUiStatus = 'waiting' | 'paid' | 'expired' | 'failed';

const vnd = new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' });

function formatClock(seconds: number): string {
  const safe = Math.max(0, seconds);
  const mm = String(Math.floor(safe / 60)).padStart(2, '0');
  const ss = String(safe % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

interface SePayPaymentCardProps {
  title?: string;
  amount: number;
  qrUrl?: string | null;
  bank?: string | null;
  accountNumber?: string | null;
  accountName?: string | null;
  code: string;
  status: SePayUiStatus;
  secondsLeft: number;
  successMessage?: string;
}

const SePayPaymentCard: React.FC<SePayPaymentCardProps> = ({
  title = 'Thanh toán đơn hàng',
  amount,
  qrUrl,
  bank,
  accountNumber,
  accountName,
  code,
  status,
  secondsLeft,
  successMessage = 'Thanh toán thành công',
}) => {
  const statusText =
    status === 'paid'
      ? successMessage
      : status === 'expired'
        ? 'Đơn hàng đã hết hạn'
        : status === 'failed'
          ? 'Không xác nhận được thanh toán'
          : `Đang chờ thanh toán · ${formatClock(secondsLeft)}`;

  return (
    <article className="overflow-hidden rounded-2xl bg-white shadow-[0_4px_24px_rgb(0_0_0_/_0.08)]">
      <header className="bg-[#C4A35A] px-5 py-4 text-center text-white">
        <div className="text-sm font-medium">{title}</div>
        <strong className="mt-1 block text-2xl font-bold">{vnd.format(amount || 0)}</strong>
      </header>
      <main className="grid gap-4 p-6">
        {status !== 'paid' && qrUrl ? (
          <img src={qrUrl} alt="QR code thanh toán" className="mx-auto h-60 w-60 object-contain" />
        ) : null}
        {status === 'waiting' ? (
          <p className="text-center text-sm text-gray-600">Mở app ngân hàng → Quét QR → Xác nhận</p>
        ) : null}
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-lg bg-slate-50 p-4 text-sm">
          {bank ? (
            <>
              <dt className="text-gray-500">Ngân hàng</dt>
              <dd className="m-0 text-right font-semibold text-gray-900">{bank}</dd>
            </>
          ) : null}
          {accountNumber ? (
            <>
              <dt className="text-gray-500">Số tài khoản</dt>
              <dd className="m-0 text-right font-mono font-semibold text-gray-900">{accountNumber}</dd>
            </>
          ) : null}
          {accountName ? (
            <>
              <dt className="text-gray-500">Chủ tài khoản</dt>
              <dd className="m-0 text-right font-semibold text-gray-900">{accountName}</dd>
            </>
          ) : null}
          <dt className="text-gray-500">Nội dung</dt>
          <dd className="m-0 text-right font-mono font-semibold text-gray-900">{code}</dd>
        </dl>
        <div
          className={`rounded-lg px-3 py-3 text-center text-sm font-semibold ${
            status === 'paid'
              ? 'bg-emerald-100 text-emerald-800'
              : status === 'expired' || status === 'failed'
                ? 'bg-red-100 text-red-800'
                : 'bg-amber-100 text-amber-900'
          }`}
        >
          {statusText}
        </div>
      </main>
    </article>
  );
};

export default SePayPaymentCard;
